import React, { useCallback, useEffect, useState } from 'react';
import notificationService from '../../services/notificationService';
import TeamsWebhookField from './TeamsWebhookField';
import Spinner from './Spinner';
import { formatDateTime } from '../../utils/datetime';
import { apiErrorMessage } from '../../utils/apiError';
import './NotificationsPanel.css';
import '../PartnerSyncTemplates.css';   // .pst-active-toggle — the shared On / Off pill
import './ProductNotificationsTab.css';

// THE NOTIFICATIONS TAB of a hotel (/hotel/:id), a chain (Chain Settings) and a partner (Partner Settings) — one
// component, three thin uses (level + id). Every product the hub offers, each with a Microsoft Teams channel that is
// told when that product has errors here (V703; checked every 5 minutes by /opt/utils/product-alerts). A hotel's errors
// also reach its chain's and its partner's channels.
//
// A channel is verified before it is kept (TeamsWebhookField: a code posted to it, typed back). Admins of the hotel /
// chain / partner change it; everyone who can see it can read it. A product that records no errors yet says so rather
// than offering a channel that would never hear anything.
const LEVEL_WORD = { hotel: 'this hotel', chain: 'this chain and its hotels', partner: 'this partner\'s chains and hotels' };

const ProductNotificationsTab = ({ level, id }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [notes, setNotes] = useState({});       // product -> { ok|error }

    const load = useCallback(async () => {
        if (!id) return;
        setError(null);
        try {
            setData(await notificationService.listProductNotifications(level, id));
        } catch (e) {
            setError(apiErrorMessage(e, 'Could not load the notifications'));
        }
    }, [level, id]);
    useEffect(() => { load(); }, [load]);

    const note = (product, n) => setNotes(prev => ({ ...prev, [product]: n }));
    const act = async (product, fn, ok) => {
        note(product, null);
        try {
            const res = await fn();
            if (res && res.products) setData(res);
            if (ok) note(product, { ok });
        } catch (e) {
            note(product, { error: apiErrorMessage(e, 'That did not work') });
            throw e;
        }
    };

    if (!id) return <p className="pnt-muted">Choose a {level} first.</p>;
    if (error) return <div className="pnt-error">{error}</div>;
    if (!data) return <div className="pnt-loading"><Spinner size="sm" /> Loading notifications…</div>;

    const canEdit = !!data.canEdit;
    return (
        <div className="pnt">
            <p className="pnt-intro">
                Tell a Microsoft Teams channel when a product has errors for {LEVEL_WORD[level]}
                {data.entityName ? ` (${data.entityName})` : ''}. Errors are checked every 5 minutes and sent as one message
                per product, with how many failed, the latest reason and a link.
                {level !== 'partner' && ' The chain\'s and partner\'s channels are told as well.'}
                {!canEdit && ' Only an admin can change these.'}
            </p>
            <div className="pnt-list">
                {data.products.map(p => (
                    <div key={p.key} className={`pnt-row${p.alerts ? '' : ' is-quiet'}`}>
                        <div className="pnt-product">
                            <div className="pnt-name">{p.name}</div>
                            <div className="pnt-what">{p.alerts ? `Tells the channel about ${p.what}.` : 'Does not send error alerts yet.'}</div>
                        </div>
                        <div className="pnt-channel">
                            {p.alerts ? (
                                <>
                                    <TeamsWebhookField
                                        value={p.teamsWebhook}
                                        disabled={!canEdit}
                                        showHelp={false}
                                        verify={(url, code) => act(p.key, () => notificationService.saveProductNotification(level, id, p.key, url, code), 'Saved — this channel will be told about errors.')}
                                        onCommit={(url) => (url ? Promise.resolve()
                                            : act(p.key, () => notificationService.removeProductNotification(level, id, p.key), null))}
                                    />
                                    {p.teamsWebhook && (
                                        <div className="pnt-meta">
                                            {canEdit && (
                                                <>
                                                    <button type="button" className={`pst-active-toggle ${p.active ? 'is-on' : 'is-off'}`}
                                                        onClick={() => act(p.key, () => notificationService.setProductNotificationActive(level, id, p.key, !p.active)).catch(() => {})}>
                                                        <span className="pst-active-dot" />{p.active ? 'On' : 'Off — not told'}
                                                    </button>
                                                    <button type="button" className="notif-send-btn"
                                                        onClick={() => act(p.key, () => notificationService.testProductNotification(level, id, p.key), 'Test sent — check the channel.').catch(() => {})}>
                                                        Send a test
                                                    </button>
                                                </>
                                            )}
                                            <span className="pnt-muted">
                                                {p.lastSentAt ? `Last alert ${formatDateTime(p.lastSentAt)}` : 'No alert sent yet'}
                                            </span>
                                        </div>
                                    )}
                                    {p.lastError && <div className="notif-error-msg">The channel refused the last alert: {p.lastError}</div>}
                                    {notes[p.key] && (notes[p.key].ok
                                        ? <div className="pnt-ok">{notes[p.key].ok}</div>
                                        : <div className="notif-error-msg">{notes[p.key].error}</div>)}
                                </>
                            ) : <span className="pnt-muted">—</span>}
                        </div>
                    </div>
                ))}
            </div>
            {canEdit && (
                <details className="notif-teams-help">
                    <summary>How to create a Teams webhook for a channel</summary>
                    <ol>
                        <li>Open the Microsoft Teams channel that should receive the alerts.</li>
                        <li>Click the <strong>•••</strong> menu next to the channel name and choose <strong>Workflows</strong>.</li>
                        <li>Search for <strong>Post to a channel when a webhook request is received</strong> and select it.</li>
                        <li>Name the workflow, pick the team and channel, and click <strong>Add workflow</strong>.</li>
                        <li>Copy the generated webhook URL, paste it beside the product and press <strong>Verify to complete setup</strong>.</li>
                    </ol>
                    <p className="notif-teams-help-note">Keep the URL private — anyone with it can post to the channel.</p>
                </details>
            )}
        </div>
    );
};

export default ProductNotificationsTab;
